/**
 * OnboardingShell — shared split-panel layout for all onboarding steps.
 *
 * Left panel: dark #0f0f0f, FINN brand, Typewriter headline, blue accent + cycle label.
 * Right panel: white (normal) or #f4f5f7 (wide), scrollable content area.
 *
 * normal — left 42% / padding 52px 56px, right centered maxWidth 520 / padding 52px 80px
 * wide   — left 28% / padding 40px 40px, right full-width / padding 40px 44px
 */
import { useState, useEffect } from 'react';

// ── Typewriter ─────────────────────────────────────────────────────────────────

function Typewriter({ text, speed = 48 }: { text: string; speed?: number }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span>
      {displayed}
      {!done && (
        <span style={{
          display: 'inline-block', width: 2, height: '1.1em',
          background: 'rgba(255,255,255,0.7)', marginLeft: 2,
          verticalAlign: 'text-bottom',
          animation: 'twBlink 0.9s step-end infinite',
        }} />
      )}
      <style>{`@keyframes twBlink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </span>
  );
}

// ── Cycle label ────────────────────────────────────────────────────────────────

function getCycleLabel(): string {
  // FINN cycle naming: Jan–Jun = T2, Jul–Dec = T1
  const half = new Date().getMonth() < 6 ? 'T2' : 'T1';
  return `PERFORMANCE REVIEW | ${half} ${new Date().getFullYear()}`;
}

// ── Component ──────────────────────────────────────────────────────────────────

interface OnboardingShellProps {
  /** Headline rendered with typewriter effect in the left dark panel */
  leftTitle: string;
  /** Wide layout: 28% left / #f4f5f7 right — used for Framework Preview & Before You Start */
  wide?: boolean;
  children: React.ReactNode;
}

export default function OnboardingShell({
  leftTitle,
  wide = false,
  children,
}: OnboardingShellProps) {
  return (
    <div style={{
      margin: '-32px',
      minHeight: '100vh',
      display: 'flex',
      fontFamily: 'Inter, sans-serif',
    }}>

      {/* ── Left dark panel ─────────────────────────────────────────── */}
      <div style={{
        width: wide ? '28%' : '42%',
        background: '#0f0f0f',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: wide ? '40px 40px' : '52px 56px',
        flexShrink: 0,
      }}>
        {/* Top: FINN brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 900, fontSize: 18, color: '#fff', letterSpacing: '0.04em' }}>FINN</span>
          <span style={{ color: '#555', fontSize: 14 }}>|</span>
          <span style={{ color: '#aaa', fontSize: 13, fontWeight: 400 }}>Performance Review Assistant</span>
        </div>

        {/* Middle: typewriter headline */}
        <div>
          <h2 style={{
            color: '#fff',
            fontSize: wide ? 26 : 34,
            fontWeight: 800,
            lineHeight: 1.3,
            margin: 0,
            letterSpacing: '-0.02em',
          }}>
            <Typewriter key={leftTitle} text={leftTitle} speed={48} />
          </h2>
        </div>

        {/* Bottom: accent bar + cycle label */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ width: 32, height: 3, background: '#0087eb', borderRadius: 2 }} />
          <span style={{
            fontSize: 11, color: 'rgba(255,255,255,0.22)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            {getCycleLabel()}
          </span>
        </div>
      </div>

      {/* ── Right panel ─────────────────────────────────────────────── */}
      <div style={{
        flex: 1,
        background: wide ? '#f4f5f7' : '#fff',
        display: 'flex',
        alignItems: wide ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: wide ? '40px 44px' : '52px 80px',
        overflowY: 'auto',
      }}>
        {wide ? (
          /* Wide: full-width column, content scrolls internally */
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            {children}
          </div>
        ) : (
          /* Normal: centered with max-width */
          <div style={{ width: '100%', maxWidth: 520 }}>
            {children}
          </div>
        )}
      </div>

    </div>
  );
}
